// src/pages/oura-report-render/index.tsx
//
// The route the PDF renderer prints (#3494, BE contract ourteam-backend#3839):
//
//     GET /oura/report/{assessment_id}?render_token=<short-lived JWT>
//
// Headless Chromium opens this with NO session cookie, waits for
// `body[data-report-ready="1"]`, and prints the page. Everything here follows
// from that:
//
//   • It lives OUTSIDE `OuraRoute`. The renderer cannot pass an auth guard, and
//     the guard is not what protects this page — the BE is. The render token is
//     scoped to one assessment id and expires in ~5 minutes
//     (`AEO_REPORT_TOKEN_TTL_SECONDS`), and `require_aeo_caller` refuses it for
//     any other assessment or route. So an invalid, expired or mismatched token
//     yields no data, and with no data this page renders nothing printable.
//
//   • The readiness marker is set ONLY after the payload has loaded. A marker
//     set on mount produces a confidently empty PDF, which is worse than a
//     failed render: the client receives a document that looks authoritative
//     and says nothing. On failure the marker is never set, the renderer times
//     out, and the assessment reports REPORT_RENDER_FAILED — an honest outcome
//     that someone can act on.

import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ClientReport, type ClientReportData } from "@oura/screens/ClientReport";
import { adaptAiVisibility, adaptBusinessAudit, adaptProbes, adaptReportMetrics } from "@oura/liveAudit";
import { getAssessment, isOuraLiveApiEnabled } from "@service/aeo";
import type { AeoAssessment, AeoBusinessDetail } from "@service/aeo";
import { catchErrorLog, fatalErrorLog } from "@util/sentry";

/**
 * The existing adapters read `latest_assessment.results`, and
 * `GET /assessments/{id}` returns that same `results_json` blob at its top level
 * (`to_status_dict()`). So the assessment is reshaped into the structure they
 * already understand rather than given a second mapping — two mappings over one
 * payload drift, and this one feeds a client-facing document.
 */
function asBusinessDetail(assessment: AeoAssessment): AeoBusinessDetail {
    return {
        domain: assessment.domain ?? "",
        business_name: assessment.business_name ?? "",
        latest_assessment: {
            assessment_id: assessment.assessment_id,
            status: assessment.status,
            captured_at: assessment.completed_at ?? assessment.created_at,
            results: assessment.results,
        },
    } as unknown as AeoBusinessDetail;
}

function toReportData(assessment: AeoAssessment): ClientReportData {
    const detail = asBusinessDetail(assessment);
    const metrics = adaptReportMetrics(detail);
    const results = detail.latest_assessment?.results as { queries?: unknown[] } | undefined;

    return {
        businessName: assessment.business_name || assessment.domain || "",
        domain: assessment.domain || "",
        // The report's city line comes from the business record, which this
        // token cannot read. Left empty rather than guessed — ClientReport omits
        // what it has no value for, and an invented locality in a client PDF is
        // exactly the kind of fabrication this document must not carry.
        city: "",
        capturedAt: assessment.completed_at || assessment.created_at,
        audit: adaptBusinessAudit(detail) ?? undefined,
        visibility: adaptAiVisibility(detail) ?? undefined,
        probes: adaptProbes(detail),
        areaScores: metrics?.areaScores,
        maturityLevel: metrics?.maturityLevel,
        maturityLabel: metrics?.maturityLabel,
        aiReadability: metrics?.aiReadability,
        sentiment: metrics?.sentiment,
        answerCoverage: metrics?.answerCoverage,
        questionsTested: Array.isArray(results?.queries) ? results!.queries!.length : 0,
    };
}

export default function OuraReportRender() {
    const { assessmentId } = useParams<{ assessmentId: string }>();
    const [searchParams] = useSearchParams();
    const renderToken = searchParams.get("render_token") || undefined;

    const [data, setData] = useState<ClientReportData | null>(null);
    const [failed, setFailed] = useState<boolean>(false);

    useEffect(() => {
        // Every `return` below ends with no PDF for a client, and the only other
        // symptom is a Playwright timeout 90s later that says nothing about the
        // cause. So each one reports, with the assessment id tagged so an
        // operator can go from "this client got no report" to the reason in one
        // search. `fatalErrorLog`, not `catchErrorLog`: the likeliest failures
        // here are 401 (expired token) and 404 (unknown assessment), which the
        // ordinary logger drops as routine noise. The token itself is never
        // logged — only whether one was present.
        const tags = { area: "oura_report_render", assessment_id: assessmentId || "missing" };

        if (!isOuraLiveApiEnabled) {
            fatalErrorLog(
                "[oura] report render refused: live API disabled",
                { assessment_id: assessmentId, reason: "VITE_ENABLE_OURA_LIVE_API is not \"true\" — would print fixture data" },
                tags,
            );
            setFailed(true);
            return;
        }
        if (!assessmentId) {
            fatalErrorLog(
                "[oura] report render aborted: no assessment id in the route",
                { path: window.location.pathname },
                tags,
            );
            setFailed(true);
            return;
        }
        if (!renderToken) {
            // Not fatal on its own — a signed-in staff member can open this page
            // with a session. Worth recording because the renderer ALWAYS sends
            // one, so its absence on a failed run says the URL was built wrong.
            catchErrorLog(`[oura] report render opened without a render_token (assessment ${assessmentId})`, "warning");
        }

        let cancelled = false;
        getAssessment(assessmentId, renderToken)
            .then((assessment) => {
                if (cancelled) return;
                // A run that never produced results has nothing to print.
                // Rendering the shell anyway would hand the client empty pages.
                if (!assessment?.results) {
                    fatalErrorLog(
                        "[oura] report render aborted: assessment carries no results",
                        {
                            assessment_id: assessmentId,
                            assessment_status: assessment?.status,
                            stage: assessment?.stage,
                            error_code: assessment?.error_code,
                        },
                        tags,
                    );
                    setFailed(true);
                    return;
                }
                // Mapping is separated from the fetch so the two cannot be
                // confused in Sentry: "the payload never arrived" and "the
                // payload arrived and we could not read it" need different fixes.
                try {
                    setData(toReportData(assessment));
                } catch (error) {
                    fatalErrorLog(
                        "[oura] report render failed while mapping the assessment payload",
                        { assessment_id: assessmentId, detail: String(error) },
                        tags,
                    );
                    setFailed(true);
                }
            })
            .catch((error) => {
                if (cancelled) return;
                // Status goes in the context, never the message: the global
                // beforeSend drops any message mentioning 404, and a stable
                // message keeps every occurrence in one Sentry issue.
                const status = (error as { response?: { status?: number } })?.response?.status;
                fatalErrorLog(
                    "[oura] report render failed: could not load the assessment",
                    {
                        assessment_id: assessmentId,
                        http_status: status ?? "none (network or timeout)",
                        had_render_token: !!renderToken,
                        detail: String(error),
                    },
                    { ...tags, http_status: String(status ?? "none") },
                );
                setFailed(true);
            });
        return () => { cancelled = true; };
    }, [assessmentId, renderToken]);

    // The quietest failure of all: the fetch never settles, so neither branch
    // above runs and neither marker is ever set. From the outside it is
    // indistinguishable from a slow render, and the only record is a Playwright
    // timeout with no cause. This fires before the renderer's 90s budget, while
    // the page still exists to report it.
    useEffect(() => {
        if (data || failed) return;
        const timer = setTimeout(() => {
            fatalErrorLog(
                "[oura] report render stalled: no marker after 60s",
                {
                    assessment_id: assessmentId,
                    had_render_token: !!renderToken,
                    detail: "the assessment request never settled — neither data-report-ready nor data-report-error was set",
                },
                { area: "oura_report_render", assessment_id: assessmentId || "missing" },
            );
        }, 60_000);
        return () => clearTimeout(timer);
    }, [data, failed, assessmentId, renderToken]);

    // Deliberately no spinner and no error page: this route exists to be
    // printed. Anything rendered here that is not the report is a page the
    // renderer might capture. It waits for the marker instead, and times out
    // if it never comes.
    if (!data) {
        return failed ? <div data-report-error="1" /> : null;
    }

    return <ClientReport data={data} />;
}
